import { AfterViewInit, ChangeDetectionStrategy, Component, OnInit, ViewChild } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { isNullOrUndefined } from '@sla-ng/core';
import { BehaviorSubject, distinctUntilChanged, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IContributionMarginLevel } from '../../../../models/contribution-margin-level.model';
import { ICostUnitGroupState } from '../../../../services/facades/master-data/cost-unit-group-state.model';
import { CostUnitGroupFacade } from '../../../../services/facades/master-data/cost-unit-group.facade';
import { ICostUnitGroupAssignmentsDialogLayout } from '../../../../models/layout/cost-unit-group-assignments-dialog-layout.model';
import { CostUnitGroupLayoutService } from '../../../../services/layout/cost-unit-group-layout.service';

@Component({
    templateUrl: './cost-unit-group-contribution-margin-level-assignments.dialog.component.html',
    styleUrls: [ './cost-unit-group-contribution-margin-level-assignments.dialog.component.scss' ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CostUnitGroupContributionMarginLevelAssignmentsDialogComponent implements OnInit, AfterViewInit {

    @ViewChild(MatSort)
    public sort!: MatSort;
    @ViewChild(MatPaginator)
    public paginator!: MatPaginator;
    public layout: ICostUnitGroupAssignmentsDialogLayout;

    public costUnitState$: Observable<ICostUnitGroupState>;
    public formGroup$: BehaviorSubject<FormGroup> = new BehaviorSubject<FormGroup>(null);
    public contributionMarginLevelSelected: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public dataSource: MatTableDataSource<IContributionMarginLevel> = new MatTableDataSource<IContributionMarginLevel>([]);

    constructor(
        private myMatDialogRef: MatDialogRef<CostUnitGroupContributionMarginLevelAssignmentsDialogComponent>,
        private myCostUnitGroupFacade: CostUnitGroupFacade,
        private myCostUnitGroupLayoutService: CostUnitGroupLayoutService,
    ) {
    }

    public ngOnInit(): void {
        this.layout = this.myCostUnitGroupLayoutService.getContributionMarginLevelAssignmentsDialogLayout();
        this.costUnitState$ = this.myCostUnitGroupFacade.subState();
        this.costUnitState$.pipe(
            map((s: ICostUnitGroupState) => s.assignedContributionMarginLevels),
            distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)))
            .subscribe((data: IContributionMarginLevel[]) => {
                this.dataSource.data = data;
            });
    }

    public ngAfterViewInit(): void {
        this.dataSource.sort = this.sort;
        this.dataSource.paginator = this.paginator;
    }

    public async assignContributionMarginLevel(): Promise<void> {
        const contributionMarginLevelId: number = this.formGroup$.value.getRawValue().selectedContributionMarginLevelId;
        await this.myCostUnitGroupFacade.assignContributionMarginLevelToGroup(contributionMarginLevelId);
        this.formGroup$.value.controls[ 'selectedContributionMarginLevelId' ].patchValue(null);
    }

    public close(): void {
        this.myMatDialogRef.close(false);
    }

    public async onDeleteContributionMarginLevelAssignment(contributionMarginLevel: IContributionMarginLevel): Promise<void> {
        await this.myCostUnitGroupFacade.deleteContributionMarginLevelAssignment(contributionMarginLevel.id);
    }

    public formGroupChange(formGroup: FormGroup): void {
        this.formGroup$.next(formGroup);

        formGroup.valueChanges.subscribe((value) => {
            if (isNullOrUndefined(value) || isNullOrUndefined(value.selectedContributionMarginLevelId)) {
                this.contributionMarginLevelSelected.next(false);
            } else {
                this.contributionMarginLevelSelected.next(true);
            }
        });
    }
}
